import { Component, EventEmitter, Input, OnInit, OnDestroy, OnChanges, Output, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { MatCheckboxChange, MatSelectChange, MatSort } from '@angular/material';

import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { map } from 'rxjs/operators/map';
import { Observable } from 'rxjs/Observable';
import { takeUntil } from 'rxjs/operators/takeUntil';
import { merge } from 'rxjs/observable/merge';
import { of } from 'rxjs/observable/of';

import { isBefore, isDate, isValid } from 'date-fns';

import { DataTableSource } from '../../../classes/DataTableSource/datatablesource';

import {
    CollegeCourseInfo,
    CollegeDepartmentInfo,
    CourseChoice,
    StudentCollegeInfo,
} from '../../../services/application/application.service';
import { DialogService } from '../../../services/dialogs/dialogs.service';
import { SplashService } from '../../../services/splash/splash.service';
import { UserInfo } from '../../../services/user/user.service';

import { CourseRequestStatusPipe } from '../../../pipes/courserequeststatus.pipe';
import { EligibleCoursesPipe } from '../../../pipes/eligible-courses.pipe';
import { YesNoPipe } from '../../../pipes/yesno.pipe';

@Component({
    selector: 'app-course-selection-panel',
    styleUrls: ['courseselectionpanel.component.css'],
    templateUrl: 'courseselectionpanel.component.html',
})
export class CourseSelectionPanelComponent implements OnInit, OnDestroy, OnChanges {
    @ViewChild(MatSort) sorter: MatSort;

    // Inputs
    @Input() CanBypassAdmin: boolean;
    @Input() Courses: CourseChoice[];
    @Input() SelectedCollege: StudentCollegeInfo;
    @Input() SelectedCollegeCourses: CollegeCourseInfo[];
    @Input() SelectedCollegeDepartments: CollegeDepartmentInfo[];
    @Input() SmallDisplay: boolean;
    @Input() User: UserInfo;

    // Outputs
    @Output() approveallcourses: EventEmitter<ApproveRequestInfo> = new EventEmitter<ApproveRequestInfo>();
    @Output() approvecourse: EventEmitter<CourseChoice> = new EventEmitter<CourseChoice>();
    @Output() eligilibity: EventEmitter<number[] | null> = new EventEmitter<number[] | null>();
    @Output() fasttrack: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() refpdf: EventEmitter<void> = new EventEmitter<void>();
    @Output() removecourse: EventEmitter<CourseChoice> = new EventEmitter<CourseChoice>();
    @Output() returnform: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() selectcourse: EventEmitter<CollegeCourseInfo> = new EventEmitter<CollegeCourseInfo>();
    @Output() setcourseref: EventEmitter<CourseChoice> = new EventEmitter<CourseChoice>();
    @Output() setcoursealtref: EventEmitter<CourseChoice> = new EventEmitter<CourseChoice>();
    @Output() undoremovecourse: EventEmitter<CourseChoice> = new EventEmitter<CourseChoice>();

    // Private
    private AllHeaders: string[] = [
        'Code',
        'CourseName',
        'Credits',
        'Required',
        'DateAdded',
        'DateRemoved',
        'DateApproved',
        'Status',
        'ReferenceNum',
        'AltReferenceNum',
        'Actions',
    ];

    // Class
    AsAdvisor: boolean;
    AsCollegeAdmin: boolean;
    AsCollegeSupport: boolean;
    AsCounselor: boolean;
    AsDeveloper: boolean;
    AsDistrictAdmin: boolean;
    AsParent: boolean;
    AsSchoolAdmin: boolean;
    AsStudent: boolean;
    CoursesData: DataTableSource<CourseChoice>;
    EligibilityByCourseGrade: boolean;
    EligibilityByCourseGradeCourses: number[];
    EligibilityByCourseGradeCoursesList: string[];
    FastTrackAdvised: boolean;
    FilteredCourses: CourseChoice[];
    HasActiveRequests: boolean;
    HasRemovedRequests: boolean;
    HasUnscheduledRequests: boolean;
    NoData: boolean;
    OrigEligCourseGrade: boolean;
    OrigEligCourses: number | null;
    ShownColumns: string[];
    ShowRemoved: boolean;
    TotalCredits: number;
    VisibleHeaders: string[];

    constructor(
        private courseRequestStatusPipe: CourseRequestStatusPipe,
        private dialogService: DialogService,
        private eligCrsPipe: EligibleCoursesPipe,
        private router: Router,
        private splashService: SplashService,
        private yesNoPipe: YesNoPipe
    ) {
        this.AsAdvisor = false;
        this.AsCollegeAdmin = false;
        this.AsCollegeSupport = false;
        this.AsCounselor = false;
        this.AsDeveloper = false;
        this.AsDistrictAdmin = false;
        this.AsParent = false;
        this.AsSchoolAdmin = false;
        this.AsStudent = false;
        this.HasActiveRequests = false;
        this.HasRemovedRequests = false;
        this.HasUnscheduledRequests = false;
        this.ShowRemoved = false;
    }

    ngOnInit() {}

    ngOnDestroy() {}

    ngOnChanges() {
        const self = this;

        if (!self.CoursesData) {
            self.CoursesData = new DataTableSource<CourseChoice>([], null, self.sorter, self.SortCourses.bind(self));
        }

        self.FilteredCourses = null;

        // Parse out the courses
        let anyActive = false;
        let anyRemoved = false;
        let anyUnscheduled = false;
        let credits = 0;

        if (self.Courses && self.Courses.length) {
            self.NoData = false;

            // Parse through the provided list to determine if there are any entries which are not removed.
            self.Courses.forEach((item) => {
                if (item.RemovedBy == null) {
                    anyActive = true;
                    credits += item.Credits;

                    if (!item.ReferenceNumber) {
                        anyUnscheduled = true;
                    }
                } else {
                    anyRemoved = true;
                }
            });
        } else {
            // Add an empty object to trigger the no data row
            self.NoData = true;
        }

        self.HasActiveRequests = anyActive;
        self.HasRemovedRequests = anyRemoved;
        self.HasUnscheduledRequests = anyUnscheduled;
        self.TotalCredits = credits;

        // Update the data source
        self.CoursesData.Update(self.Courses || [{}]);

        if (self.User) {
            self.AsAdvisor = self.User.IsAdvisor;
            self.AsCollegeAdmin = self.User.IsCollegeAdmin;
            self.AsCollegeSupport = self.User.IsCollegeSupport;
            self.AsCounselor = self.User.IsCounselor;
            self.AsDeveloper = self.User.IsDeveloper;
            self.AsDistrictAdmin = self.User.IsDistrictAdmin;
            self.AsParent = self.User.IsParent;
            self.AsSchoolAdmin = self.User.IsSchoolAdmin;
            self.AsStudent = self.User.IsStudent;
        }

        self.EligibilityByCourseGrade = self.SelectedCollege && self.SelectedCollege.EligibilityByCourseGrade != null;
        self.OrigEligCourseGrade = self.EligibilityByCourseGrade;
        self.OrigEligCourses = self.SelectedCollege && self.SelectedCollege.EligibilityByCourseGrade;
        self.SetupEligibilityCourses(self.SelectedCollege && self.SelectedCollege.EligibilityByCourseGrade);

        self.FastTrackAdvised = self.SelectedCollege && self.SelectedCollege.FastTrackAdvised;

        self.UpdateFiltering();
    }

    // #region Approval All Courses Method

    ApproveAllCourses(AsBypassAdmin?: boolean) {
        this.approveallcourses.emit({ ActionNeeded: true, BypassAdmin: AsBypassAdmin });
    }

    // #endregion

    // #region Approve Course Choice Method

    ApproveCourseChoice(course: CourseChoice) {
        if (!course) {
            return;
        }

        this.approvecourse.emit(course);
    }

    // #endregion

    // #region Checkbox Handler

    CheckboxHandler(event: MatCheckboxChange) {
        if (!event) return;

        if (!event.checked) {
            // Is there anything set as courses? If so, we must get a confirmation before wiping things out.
            (this.EligibilityByCourseGradeCourses && this.EligibilityByCourseGradeCourses.length
                ? this.dialogService.OpenConfirmationDialog({
                      TitleText: 'Confirmation Requested',
                      MessageText: `You are about to turn off the option for listing eligibility courses. The following courses are currently selected for this student/term:<br /><ul>${this.EligibilityByCourseGradeCoursesList.reduce(
                          (p, c) => p + `<li>${c}</li>`,
                          ''
                      )}</ul><br />If you proceed, the system will remove all of these eligibility courses from the student for this term. Are you sure you wish to remove these courses?`,
                      OKText: 'Yes - Remove All',
                      CancelText: 'No - Cancel',
                  })
                : of<boolean>(true)
            ).subscribe((result) => {
                if (!result) {
                    // User is canceling. Don't change the checkbox.
                    this.EligibilityByCourseGrade = true;
                    return;
                }

                // The user opted to remove.
                this.eligilibity.emit(null);

                this.SetupEligibilityCourses(null);
            });
        } else {
            // Checkbox is on, so pop the dialog box to collect the courses.
            this.dialogService
                .OpenEligibleCoursesDialog({
                    TitleText: 'Select Eligibility Courses',
                    MessageText:
                        'Please select one or more courses from the list provided below which are being used to establish eligibility for the student:',
                    Courses: this.EligibilityByCourseGradeCourses,
                })
                .subscribe((result) => {
                    if (!result) {
                        // The user canceled. We turn the checkbox back off in this case and do nothing.
                        this.EligibilityByCourseGrade = false;
                        return;
                    }

                    // We have courses. Emit the choices that were selected to the parent as a single value.
                    this.eligilibity.emit(result);
                });
        }
    }

    // #endregion

    // #region Download Ref PDF

    DownloadRefPDF() {
        this.refpdf.emit();
    }

    // #endregion

    // #region Eligibility Change Handler Method

    EligibilityChangeHandler() {
        // Pop the dialog so changes can be made.
        this.dialogService
            .OpenEligibleCoursesDialog({
                TitleText: 'Select Eligibility Courses',
                MessageText:
                    'Please select one or more courses from the list provided below which are being used to establish eligibility for the student:',
                Courses: this.EligibilityByCourseGradeCourses,
            })
            .subscribe((result) => {
                if (!result) return;

                // We have courses. Emit the choices that were selected to the parent as a single value.
                this.eligilibity.emit(result);
            });
    }

    // #endregion

    // #region Fast Track Change Handler Method

    FastTrackChangeHandler(event: MatCheckboxChange) {
        if (!event) {
            return;
        }

        // Emit the new value
        this.fasttrack.emit(event.checked);
    }

    // #endregion

    // #region Remove Course Choice Method

    RemoveCourseChoice(course: CourseChoice) {
        if (!course) {
            return;
        }

        this.removecourse.emit(course);
    }

    // #endregion

    // #region Return Form Method

    ReturnForm() {
        this.returnform.emit(true);
    }

    // #endregion

    // #region Select Course Method

    SelectCourse() {
        const self = this;

        // Pop up the dialog to make the selection, and wait for the return.
        self.dialogService
            .OpenCourseChoiceDialog({
                TitleText: 'Select course...',
                MessageText:
                    `Use the selection boxes below to choose a course department and specific course to request. You may ` +
                    `also do a free-text search by using the provided space below, which will look for any courses which have ` +
                    `the provided text within their course name or course description. Once a course is selected, the ` +
                    `information for the course will be displayed for your review. To add the selected course to your list of ` +
                    `requested courses for this college, click the "Select Course" button. If you wish to exit without adding ` +
                    `a course, click the "Cancel" button.` +
                    (self.AsStudent
                        ? `<br /><br /><strong>Note: </strong> If the current term you are adding a course to has been ` +
                          `previously approved by your school counselor or a college advisor, adding a new course <strong><u>` +
                          `<em>will</em></u></strong> require your counselor to re-approve the term's requests. This will also ` +
                          `prevent your college advisor from seeing this term until approval has been re-granted.`
                        : ''),
                Departments: self.SelectedCollegeDepartments,
                Courses: self.SelectedCollegeCourses,
                CanMakeAdoptionRequest: self.AsCounselor || self.AsSchoolAdmin,
            })
            .subscribe((coursechosen) => {
                if (!coursechosen) {
                    return;
                }

                // Is this an adoption request? If so, we need to ask and find out if they want to go to that form.
                if (coursechosen.RequestAdoption) {
                    // Get a confirmation before proceeding.
                    self.dialogService
                        .OpenConfirmationDialog({
                            TitleText: 'Confirmation Requested',
                            MessageText:
                                `You are about to leave this page and go to the Course Adoption Request form. ` +
                                `Are you sure you wish to go to that form?`,
                            OKText: 'Yes',
                            CancelText: 'No',
                        })
                        .subscribe((result) => {
                            if (result === true) {
                                self.router.navigate(['/adoptionrequest', coursechosen.CourseChoice.DualEnrollCollegeCourseID]);
                            }
                        });
                } else {
                    // Emit what was chosen to go over to the parent.
                    self.selectcourse.emit(coursechosen.CourseChoice);
                }
            });
    }

    // #endregion

    // #region Set Course Reference Number

    SetCourseReferenceNumber(course: CourseChoice) {
        if (!course) {
            return;
        }

        this.setcourseref.emit(course);
    }

    // #endregion

    // #region Set Course Alt Reference Number

    SetCourseAltReferenceNumber(course: CourseChoice) {
        if (!course) {
            return;
        }

        this.setcoursealtref.emit(course);
    }

    // #endregion

    // #region Setup Eligibility Courses

    SetupEligibilityCourses(coursesVal: number | null) {
        if (coursesVal == null) {
            this.EligibilityByCourseGradeCourses = [];
            this.EligibilityByCourseGradeCoursesList = [];
            return;
        }

        // Parse out the bitfield into distinct values, then convert to text as needed.
        const crs = [] as number[];
        const crslist = [] as string[];

        if (coursesVal & 1) {
            crs.push(1);
            crslist.push('AICE Math 1 AS');
        }
        if (coursesVal & 2) {
            crs.push(2);
            crslist.push('AICE MA&PR&ST 2 AL');
        }
        if (coursesVal & 4) {
            crs.push(4);
            crslist.push('Algebra 1 / Algebra 1 Honors');
        }
        if (coursesVal & 8) {
            crs.push(8);
            crslist.push('Algebra 2 / Algebra 2 Honors');
        }
        if (coursesVal & 16) {
            crs.push(16);
            crslist.push('Geometry / Geometry Honors');
        }
        if (coursesVal & 32) {
            crs.push(32);
            crslist.push('IB PRECALCULUS');
        }
        if (coursesVal & 64) {
            crs.push(64);
            crslist.push('Pre-AICE Math 2 IG');
        }
        if (coursesVal & 128) {
            crs.push(128);
            crslist.push('Pre-AICE Math 3 IG');
        }
        if (coursesVal & 256) {
            crs.push(256);
            crslist.push('PRECALCULUS Honors');
        }
        if (coursesVal & 512) {
            crs.push(512);
            crslist.push('English 4 / English 4 Honors');
        }
        if (coursesVal & 1024) {
            crs.push(1024);
            crslist.push('IB MYP Algebra 1');
        }

        this.EligibilityByCourseGradeCourses = crs;
        this.EligibilityByCourseGradeCoursesList = crslist;
    }

    // #endregion

    // #region Show Course Description Method

    ShowCourseDescription(course: any) {
        const self = this;

        // In order to show the info, we must find the correct college course info tied to this course choice.
        let chosenCourse: CollegeCourseInfo = null;

        if (!self.SelectedCollegeCourses || !self.SelectedCollegeCourses.length) {
            self.splashService.SnackbarError('Unable to load course information for this college at this time.');
            return;
        }

        self.SelectedCollegeCourses.every((item) => {
            if (course.CollegeCourseID === item.DualEnrollCollegeCourseID) {
                chosenCourse = item;
                return false; // Exit loop.
            }
            return true; // Loop.
        });

        if (!chosenCourse) {
            self.splashService.SnackbarError('Unable to load course information for this college at this time.');
            return;
        }

        // Pop up a dialog holding the course description information for the selected course.
        self.dialogService
            .OpenCourseDescriptionDialog({
                TitleText: 'Course Information',
                MessageText: '',
                Course: chosenCourse,
            })
            .subscribe((result) => {});
    }

    // #endregion

    // #region Sort Courses Method

    SortCourses(First: CourseChoice, Second: CourseChoice) {
        const self = this;

        let PropA: string | number | Date;
        let PropB: string | number | Date;

        switch (self.sorter.active) {
            case 'Code':
                [PropA, PropB] = [First.FullCode, Second.FullCode];
                break;
            case 'CourseName':
                [PropA, PropB] = [First.Name, Second.Name];
                break;
            case 'Credits':
                [PropA, PropB] = [First.Credits, Second.Credits];
                break;
            case 'Required':
                [PropA, PropB] = [self.yesNoPipe.transform(First.PriorityRequest), self.yesNoPipe.transform(Second.PriorityRequest)];
                break;
            case 'DateAdded':
                [PropA, PropB] = [First.DateCreated, Second.DateCreated];
                break;
            case 'DateRemoved':
                [PropA, PropB] = [First.DateRemoved, Second.DateRemoved];
                break;
            case 'DateApproved':
                [PropA, PropB] = [First.DateScheduled, Second.DateScheduled];
                break;
            case 'Status':
                [PropA, PropB] = [
                    self.courseRequestStatusPipe.transform(First, self.SelectedCollege.FormStatus),
                    self.courseRequestStatusPipe.transform(Second, self.SelectedCollege.FormStatus),
                ];
                break;
            case 'ReferenceNum':
                [PropA, PropB] = [First.ReferenceNumber, Second.ReferenceNumber];
                break;
        }

        if (isDate(PropA) || isDate(PropB)) {
            let baseSort = 0;

            if (isDate(PropA) && !isDate(PropB)) {
                baseSort = 1;
            } else if (isDate(PropB) && !isDate(PropA)) {
                baseSort = -1;
            } else if (isBefore(PropA, PropB)) {
                baseSort = -1;
            } else if (isBefore(PropB, PropA)) {
                baseSort = 1;
            }

            return baseSort * (self.sorter.direction === 'asc' ? 1 : -1);
        } else {
            const ValueA = PropA != null && PropA !== '' ? (isNaN(+PropA) ? PropA : +PropA) : PropA;
            const ValueB = PropB != null && PropB !== '' ? (isNaN(+PropB) ? PropB : +PropB) : PropB;

            return (ValueA < ValueB ? -1 : 1) * (self.sorter.direction === 'asc' ? 1 : -1);
        }
    }

    // #endregion

    // #region Undo Remove Course Choice Method

    UndoRemoveCourseChoice(course: CourseChoice) {
        if (!course) {
            return;
        }

        this.undoremovecourse.emit(course);
    }

    // #endregion

    // #region Update Filtering Method

    UpdateFiltering() {
        const self = this;

        const fcourses = (this.Courses || []).filter((item) => {
            return this.ShowRemoved || !item.RemovedBy;
        });

        self.NoData = !fcourses.length;

        self.FilteredCourses = fcourses;
        self.CoursesData.Update(self.FilteredCourses);

        // Set up headers for display
        self.VisibleHeaders = [];

        if (self.SmallDisplay) {
            self.VisibleHeaders.push('SmallDisplay');
        } else {
            self.VisibleHeaders.push('Code', 'CourseName', 'Credits', 'Required', 'DateAdded');
            if (self.ShowRemoved) {
                self.VisibleHeaders.push('DateRemoved');
            } else {
                self.VisibleHeaders.push('DateApproved');
            }
            self.VisibleHeaders.push('Status', 'ReferenceNum', 'AltReferenceNum');

            if (
                self.SelectedCollege &&
                (self.SelectedCollege.FormStatus !== 10 || self.AsDistrictAdmin) &&
                self.SelectedCollege.HistoricalRecord !== true
            ) {
                self.VisibleHeaders.push('Actions');
            }
        }

        // Set which visible shown columns are available. Show the NoCourses entry if either there are no courses, or
        // the only courses are removed and we're not showing those.
        self.ShownColumns =
            self.NoData || (!self.HasActiveRequests && !self.ShowRemoved)
                ? ['NoCourses']
                : self.SmallDisplay
                ? ['SmallDisplay']
                : self.VisibleHeaders.slice();
    }

    // #endregion
}

// #region Custom Classes

// #region Approve Request Info Class

export class ApproveRequestInfo {
    ActionNeeded?: boolean;
    BypassAdmin?: boolean;
}

// #endregion

// #endregion



// WEBPACK FOOTER //
// ./src/app/ui/courseselectionpanel/courseselectionpanel.component.ts